import { DateTime, Interval } from 'luxon';
import { useAPI } from '../clients/quipli-service/quipli-service-client';
import { toast } from '../util/toasts';
import { useEffect, useState } from 'react';
import { DailyHours, Location } from '../clients/quipli-service/location/location';
import { Dictionary } from 'lodash';

export interface LocationInterval {
  disabledIntervals: Interval[];
  enabledIntervals: Interval[];
}

interface UseLocationInterval {
  isLoading: boolean;
  locationIntervals: Dictionary<LocationInterval>;
}

/*
  Useful for things like the inventory calendar, so we can see if a datetime is a valid
  time in any particular location for multi location lookups.
*/
export const isTimeDisabledInAllLocations = (
  time: DateTime,
  disabledIntervals: Dictionary<LocationInterval>,
): boolean => {
  if (disabledIntervals === null) {
    return false;
  }
  if (Object.keys(disabledIntervals).length === 0) {
    return false;
  }

  // Go through each location's disabled times
  // and see if the time falls within any of them

  let anyLocationValidTime = false;
  Object.values(disabledIntervals).forEach((locationInterval) => {
    if (locationInterval == null) {
      return false;
    }

    const locationDisabledIntervals = locationInterval.disabledIntervals;
    if (locationDisabledIntervals === null || locationDisabledIntervals.length === 0) {
      anyLocationValidTime = true;
      return true;
    }

    let isTimeValidForLocation = true;
    for (const interval of locationDisabledIntervals) {
      const hourlyInterval = Interval.fromDateTimes(
        interval.start.hour !== 0 ? interval.start.startOf('hour') : interval.start,
        // If the opening time is a 00:30, then we want to round down to the hour
        interval.start.hour === 0 && interval.end.minute !== 0
          ? interval.end.startOf('hour').minus({ minutes: 1 })
          : interval.end,
      );

      if (hourlyInterval.contains(time)) {
        isTimeValidForLocation = false;
      }
    }
    if (isTimeValidForLocation) {
      anyLocationValidTime = true;
    }
  });

  return !anyLocationValidTime;
};

const dailyHoursToIntervals = (
  dailyHours: DailyHours[],
  location: Location,
  baseDateTime: DateTime | null,
  enableFullWhenDayDisabled: boolean,
): Interval[] => {
  const timeZone = location.timezone;

  const now = DateTime.now().setZone(timeZone);
  const startOfIntervalOneYearAgo = now.minus({ years: 1 }).startOf('day');
  const endOfIntervalTwoYearsFromNow = now.plus({ years: 2 }).endOf('day');

  const disabledIntervals: Interval[] = [];

  let entireInterval = Interval.fromDateTimes(startOfIntervalOneYearAgo, endOfIntervalTwoYearsFromNow);
  if (baseDateTime !== null) {
    entireInterval = Interval.fromDateTimes(baseDateTime.startOf('day'), baseDateTime.endOf('day'));
  }

  const eachDay = entireInterval.splitBy({ days: 1 });

  eachDay.forEach((day) => {
    const dayOfWeek = day.start.weekday;
    const wholeDayDuration = Interval.fromDateTimes(day.start.startOf('day'), day.start.endOf('day'));
    const dailyHoursForThisDay = dailyHours.filter((dh) => dh.numericDayOfWeek === dayOfWeek);

    if (!(dailyHoursForThisDay === undefined || dailyHoursForThisDay.length === 0)) {
      let currentStart = wholeDayDuration.start;

      const sortedDailyHours = dailyHoursForThisDay.sort((a, b) => {
        const startHourA = a.rentalStart.start;
        const startHourB = b.rentalStart.start;

        if (startHourA < startHourB) {
          return -1;
        } else if (startHourA > startHourB) {
          return 1;
        } else {
          return 0;
        }
      });

      sortedDailyHours.forEach((sdh) => {
        if (sdh.isEnabled === false) {
          return;
        }
        const startDateTime = day.start.set({
          hour: parseInt(sdh.rentalStart.start.split(':')[0]),
          minute: parseInt(sdh.rentalStart.start.split(':')[1]),
          second: 0,
          millisecond: 0,
        });

        const endDateTime = day.start
          .set({
            hour: parseInt(sdh.rentalEnd.end.split(':')[0]),
            minute: parseInt(sdh.rentalEnd.end.split(':')[1]),
            second: 0,
            millisecond: 0,
          })
          .plus({ minutes: 1 });

        const enabledInterval = Interval.fromDateTimes(startDateTime, endDateTime);

        if (currentStart < enabledInterval.start) {
          // Add the gap as a disabled interval
          disabledIntervals.push(Interval.fromDateTimes(currentStart, enabledInterval.start));
        }
        // Move the current start to the end of the current enabled interval
        currentStart = enabledInterval.end > currentStart ? enabledInterval.end : currentStart;
      });

      if (currentStart < wholeDayDuration.end) {
        disabledIntervals.push(Interval.fromDateTimes(currentStart, wholeDayDuration.end));
      }
    } else {
      if (!enableFullWhenDayDisabled) {
        disabledIntervals.push(wholeDayDuration);
      }
    }
  });

  return disabledIntervals;
};

const storeHoursFromLocationToIntervals = (
  locations: Location[],
  baseDateTime: DateTime | null,
  enableFullWhenDayDisabled: boolean,
): Dictionary<LocationInterval> => {
  const locationIntervals: Dictionary<LocationInterval> = {};
  if (locations === null) {
    return locationIntervals;
  }
  locations.forEach((location) => {
    const locationId = location.id;
    const locationWorkingHours = location.hoursOfOperation;
    const disabledIntervals = dailyHoursToIntervals(
      locationWorkingHours,
      location,
      baseDateTime,
      enableFullWhenDayDisabled,
    );
    locationIntervals[locationId] = {
      disabledIntervals,
      enabledIntervals: [],
    };
  });

  return locationIntervals;
};

export const useLocationInterval = (
  locations: Location[] | null,
  baseDateTime: DateTime | undefined | null,
  enableFullWhenDayDisabled: boolean = false,
): UseLocationInterval => {
  const { getConfiguration } = useAPI();

  const [isLoading, setIsLoading] = useState(true);
  const [locationIntervals, setLocationIntervals] = useState<Dictionary<LocationInterval>>({});
  const [lastBaseTime, setLastBaseTime] = useState<DateTime | null>(null);

  const initLoad = async () => {
    try {
      // Right now, only concerned with location-specific intervals
      if (locations !== null && baseDateTime !== undefined) {
        if (lastBaseTime !== null && lastBaseTime === baseDateTime) {
          return;
        }
        setLastBaseTime(baseDateTime);
        setIsLoading(true);
        const config = await getConfiguration('CONF_USE_STORE_HOURS_DATE_PICK');
        if (config?.value === '1') {
          const locationIntervals = storeHoursFromLocationToIntervals(
            locations,
            baseDateTime,
            enableFullWhenDayDisabled,
          );
          setLocationIntervals(locationIntervals);
        }
      }
    } catch (e) {
      console.log(e);
      toast('Failed to load location intervals', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initLoad();
  }, [locations, baseDateTime]);

  return { isLoading, locationIntervals };
};
